import { useEffect, useState } from "react";
import { FiCopy, FiCheckCircle } from "react-icons/fi";
import api from "../../services/api";

const ReferralInfo = () => {
  const [copied, setCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [referralCode, setReferralCode] = useState("-");
  const [referralLink, setReferralLink] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    const fetchReferralInfo = async () => {
      setIsLoading(true);
      setFetchError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setFetchError("Missing access token.");
          return;
        }

        const response = await api.get("/referral-info", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data?.status !== "success") {
          setFetchError("Failed to load referral info.");
          return;
        }

        const data = response.data?.data;
        const code = data?.referral_code || "-";
        const link = data?.referral_link || "";
        const allowed = data?.is_allowed === true;
        if (allowed) {
          setReferralCode(code);
          setReferralLink(link);
        } else {
          setReferralCode("-");
          setReferralLink("");
        }
        if (!allowed && data?.message) {
          setFetchError(data.message);
        }
      } catch (error) {
        setFetchError("Failed to load referral info.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchReferralInfo();
  }, []);

  const handleCopy = async () => {
    if (!referralLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      setCopied(false);
    }
  };

  const handleCopyCode = async () => {
    if (!referralCode || referralCode === "-") {
      return;
    }
    try {
      await navigator.clipboard.writeText(referralCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 1800);
    } catch (error) {
      setCodeCopied(false);
    }
  };

  return (
    <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
            Connections
          </p>
          <h2 className="mt-1 text-2xl font-semibold text-slate-900">
            Referral Info
          </h2>
        </div>
        <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-[var(--brand-orange-dark)]">
          Shareable
        </span>
      </div>
      <div className="mt-6 space-y-4 text-sm">
        <div className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">
              Your Referral Code
            </p>
            <div className="mt-1 flex items-center gap-3">
              <p className="text-base font-semibold text-slate-800">
                {isLoading ? "Loading..." : referralCode}
              </p>
              <button
                type="button"
                onClick={handleCopyCode}
                disabled={!referralCode || referralCode === "-"}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {codeCopied ? <FiCheckCircle /> : <FiCopy />}
                {codeCopied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
          <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-400 shadow-sm">
            {fetchError ? "Unavailable" : "Active"}
          </span>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-slate-400">
            Your Referral Link
          </p>
          <p className="mt-2 break-all text-base font-semibold text-[var(--brand-blue)]">
            {isLoading
              ? "Loading..."
              : referralLink || "Your referral link will show once your account is active."}
          </p>
        </div>
        {fetchError && (
          <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {fetchError}
          </div>
        )}
        <button
          type="button"
          onClick={handleCopy}
          disabled={!referralLink}
          className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--brand-orange)] px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
        >
          {copied ? <FiCheckCircle /> : <FiCopy />}
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
};

export default ReferralInfo;
